import { SVGProps } from 'react';
import { useTheme } from '@mui/material';

const RADIUS = 10;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

interface StepProgressRingProps extends Omit<SVGProps<SVGSVGElement>, 'ref'> {
    /** How far through the step the member is, from 0 to 1. */
    progress: number;
    size?: number;
}

/**
 * The in-progress step icon: a grey track with a green arc that fills clockwise from the top as the
 * member moves through the step's screens.
 */
export default function StepProgressRing({ progress, size = 20, style, ...svgProps }: Readonly<StepProgressRingProps>) {
    const theme = useTheme();
    const clamped = Math.min(Math.max(progress, 0), 1);

    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            aria-hidden // The step's label sits beside it; the arc is a visual cue only.
            data-testid="StepProgressRing"
            style={{ transform: 'rotate(-90deg)', flexShrink: 0, ...style }}
            {...svgProps}>
            <circle cx="12" cy="12" r={RADIUS} stroke={theme.palette.grey[200]} strokeWidth="3" fill="none" />
            <circle
                cx="12"
                cy="12"
                r={RADIUS}
                stroke={theme.palette.green[400]}
                strokeWidth="3"
                fill="none"
                strokeDasharray={CIRCUMFERENCE}
                strokeDashoffset={CIRCUMFERENCE * (1 - clamped)}
                style={{ transition: 'stroke-dashoffset 0.5s ease-out' }}
            />
        </svg>
    );
}
